/**
 * /app/{tenantSlug}/loans/approvals — Loan approval queue (migration 064).
 *
 * Finance-only convenience view: the status='requested' list with inline
 * approve / reject. A non-finance user is shown an access notice (the
 * backend role-filters anyway — this is UX). Reuses the shared LoanTable
 * row link to the detail page where the full action set lives.
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Inbox } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { isAccountingAdmin } from "@/lib/nav";
import { useLoans } from "@/hooks/use-loans";
import { Button } from "@/components/ui/button";
import { LoanTable } from "./app.$tenantSlug.loans.index";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/loans/approvals",
)({
  component: LoanApprovalsRoute,
});

function LoanApprovalsRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const finance = isAccountingAdmin(user?.role);

  const { data: loans, isLoading } = useLoans({ status: "requested" });

  if (!finance) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">
          The approval queue is available to finance roles only.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Inbox aria-hidden className="h-6 w-6 text-primary" />
          Loan approval queue
        </h1>
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/loans" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            All loans
          </Link>
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Pending loan and advance requests. Open a request to approve, reject,
        or disburse it.
      </p>
      <LoanTable
        loans={loans}
        isLoading={isLoading}
        tenantSlug={tenantSlug}
        showEmployee
      />
    </div>
  );
}
